import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  BusinessPlanningCategoryType,
  IBusinessPlanningCategoryProjection,
} from "../../types/types";
import {
  formatPercentage,
  formatTransactionCount,
} from "../../utils/BusinessPlanningCalculator";
import { formatCurrency } from "../../utils/common";
import { getColorForId } from "../../utils/chartColors";

interface CategoryProjectionCardProps {
  categoryProjection: IBusinessPlanningCategoryProjection;
  projectedGci: number;
  showCompleted?: boolean;
}

const categoryLabels: Record<BusinessPlanningCategoryType, string> = {
  type: "Type",
  source: "Source",
  reason: "Reason",
  zipCode: "Zip Code",
  county: "County",
  customArea: "Custom Area",
};

// Bottom-aligned so single-line headers line up with the two-line ones
const headerCellSx = {
  fontWeight: "bold",
  fontSize: "0.75rem",
  lineHeight: 1.2,
  verticalAlign: "bottom",
};

// Numbers stay on one line; only the Category name column wraps
const valueCellSx = { fontSize: "0.75rem", whiteSpace: "nowrap" };

export const CategoryProjectionCard: React.FC<CategoryProjectionCardProps> = ({
  categoryProjection,
  projectedGci,
  showCompleted = false,
}) => {
  // Prepare data for pie chart (skip 0% rows, e.g. "Other" made only of new current-year items)
  const pieData = categoryProjection
    .projections!.filter((proj) => (proj.gciPercentage || 0) > 0)
    .map((proj) => ({
      id: proj.id,
      name: proj.name,
      value: proj.gciPercentage,
      gciAmount: (proj.gciPercentage! / 100) * projectedGci,
    }));

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: 1,
            p: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {data.name}
          </Typography>
          <Typography variant="body2">
            {formatPercentage(data.value)} of GCI
          </Typography>
          <Typography variant="body2" color="primary">
            {formatCurrency(data.gciAmount)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card elevation={2} sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {
            categoryLabels[
              categoryProjection.category as BusinessPlanningCategoryType
            ]
          }
        </Typography>

        {/* Pie Chart */}
        <Box sx={{ height: 250, mb: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                labelLine={false}
                label={({ name, percent }) =>
                  percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""
                }
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColorForId(entry.id || entry.name || index)}
                    stroke="#ffffff"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ fontSize: "11px" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Projection Details Table */}
        <TableContainer>
          {/* Tighter side padding so all sub-columns fit when three cards share a row */}
          <Table size="small" sx={{ "& .MuiTableCell-root": { px: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Category</TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Avg Price
                </TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Comm %
                </TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Trans.
                  <br />
                  Needed
                </TableCell>
                {showCompleted && (
                  <TableCell align="right" sx={headerCellSx}>
                    Trans.
                    <br />
                    Compl.
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {categoryProjection.projections?.map((proj, index) => (
                <TableRow key={proj.id}>
                  <TableCell sx={{ fontSize: "0.75rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: getColorForId(
                            proj.id || proj.name || index,
                          ),
                        }}
                      />
                      {proj.name}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={valueCellSx}>
                    {proj.avgPrice !== undefined
                      ? formatCurrency(proj.avgPrice)
                      : "-"}
                  </TableCell>
                  <TableCell align="right" sx={valueCellSx}>
                    {proj.avgCommissionPct !== undefined
                      ? formatPercentage(proj.avgCommissionPct)
                      : "-"}
                  </TableCell>
                  <TableCell align="right" sx={valueCellSx}>
                    {formatTransactionCount(proj.transactionsNeeded!)}
                  </TableCell>
                  {showCompleted && (
                    <TableCell
                      align="right"
                      sx={{ ...valueCellSx, fontWeight: "bold" }}
                    >
                      {proj.transactionsCompleted !== undefined
                        ? proj.transactionsCompleted
                        : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary */}
        <Box sx={{ mt: 2, pt: 1, borderTop: "1px solid #eee" }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Total Transactions Needed:</strong>{" "}
            {formatTransactionCount(
              categoryProjection.projections?.reduce(
                (sum, p) => sum + (p.transactionsNeeded || 0),
                0,
              ) || 0,
            )}
          </Typography>
          {showCompleted && (
            <Typography variant="body2" color="text.secondary">
              <strong>Total Transactions Completed:</strong>{" "}
              {categoryProjection.projections?.reduce(
                (sum, p) => sum + (p.transactionsCompleted || 0),
                0,
              ) || 0}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
